import { describeApiError, type Room } from '@brush/api-client';
import { ErrorNotice } from '@brush/ui';
import { Award, Sparkles, TrendingUp, type LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { canLink, nameProblem } from '../operators/assign';
import { operatorLinkNote, operatorReadOf } from '../operators/column';
import {
  NOTHING_PENDING,
  appliedNote,
  discardNote,
  pendingAfter,
  savePlan,
  unsavedLabel,
  type PendingOperators,
  type SaveStep,
} from '../operators/pending';
import { roleNoticeText } from '../operators/assign';
import { roleGrantUrl } from '../operators/portal';
import {
  useCreateRoom,
  useDeleteRoom,
  useLinkRoomOperators,
  usePatchRoom,
  useRemoveRoomOperator,
  useRenameRoomOperator,
  useRoomOperators,
} from '../queries';
import { clockOf, gamingDayNote, minutesOf } from '../rooms/gamingDay';
import { OperatorsSection } from './OperatorsSection';

const inputClass =
  'w-full rounded-lg border border-ink-800/15 px-3 py-2 text-sm text-ink-950 outline-none focus:border-felt-500';

/**
 * The fields this dialog owns. Deliberately not the whole room:
 *
 * `status` is toggled from the table, and having it in two places would mean two answers to one
 * question. `defaultTags` belongs to the room console, and — more importantly — a PATCH sending
 * `defaultTags: []` from a form that never showed them would silently wipe a room's tag set, so
 * it is left out of the payload entirely rather than round-tripped blank.
 */
interface RoomDraft {
  name: string;
  location: string;
  primaryColor: string;
  accentColor: string;
  logo: string;
  canLeaderboard: boolean;
  canFantasy: boolean;
  canRatings: boolean;
  /** IANA id. Blank on a new room means "the platform's default", which the server applies. */
  timeZone: string;
  /** The gaming-day start as the HH:mm the time input holds; minutes only on the way out. */
  gamingDayStart: string;
  isInternal: boolean;
}

const draftFrom = (room: Room | null): RoomDraft => ({
  name: room?.name ?? '',
  location: room?.location ?? '',
  primaryColor: room?.primaryColor ?? '#b91c1c',
  accentColor: room?.accentColor ?? '#f59e0b',
  logo: room?.logo ?? '♠',
  canLeaderboard: room?.canLeaderboard ?? true,
  canFantasy: room?.canFantasy ?? true,
  canRatings: room?.canRatings ?? true,
  timeZone: room?.timeZone ?? '',
  gamingDayStart: clockOf(room?.gamingDayStartMinutes ?? 0),
  isInternal: room?.isInternal ?? false,
});

/**
 * The draft as the API takes it. A separate step rather than the draft spread into the request,
 * because two fields are held in the form's terms — the gaming day as a clock time, the zone as
 * possibly blank — and a spread would send those as they are. Only called once the blocker has
 * passed, so the start always reads back as minutes here.
 */
const requestOf = (draft: RoomDraft) => ({
  name: draft.name,
  location: draft.location,
  primaryColor: draft.primaryColor,
  accentColor: draft.accentColor,
  logo: draft.logo,
  canLeaderboard: draft.canLeaderboard,
  canFantasy: draft.canFantasy,
  canRatings: draft.canRatings,
  timeZone: draft.timeZone.trim() === '' ? null : draft.timeZone.trim(),
  gamingDayStartMinutes: minutesOf(draft.gamingDayStart) ?? 0,
  isInternal: draft.isInternal,
});

/**
 * The zones this browser knows, offered as suggestions and never as the list of what is allowed:
 * the server validates against the platform's own database (<code>RoomTimeZone.Validate</code>), and
 * a browser's list is a copy of somebody else's data. Empty where the runtime cannot say.
 */
const knownZones: readonly string[] =
  typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];

const same = (a: RoomDraft, b: RoomDraft) =>
  (Object.keys(a) as (keyof RoomDraft)[]).every((k) => a[k] === b[k]);

/**
 * A room, and who may operate it — one dialog, one Save.
 *
 * <p>
 * <b>Operators live here rather than in a panel of their own, and the room picker went with the
 * move.</b> The panel's first act was asking which room; the room is the dialog's, so that question
 * had already been answered once. What it buys beyond the click is that a room being REGISTERED can
 * stage operators against a room that does not exist yet — the panel could not, because there was
 * nothing to pick.
 * </p>
 * <p>
 * <b>Save is a sequence, not a transaction.</b> Four writes across four endpoints, in the order
 * <code>savePlan</code> gives them, each dropped from the draft as it lands. See that module for
 * why the order matters; see <code>appliedNote</code> for what the footer says once any of it has.
 * </p>
 */
export function RoomModal({
  room,
  onCreated,
  onClose,
}: {
  room: Room | null;
  onCreated: (created: Room) => void;
  onClose: () => void;
}) {
  const createRoom = useCreateRoom();
  const patchRoom = usePatchRoom();
  const deleteRoom = useDeleteRoom();
  const linkOperators = useLinkRoomOperators();
  const renameOperator = useRenameRoomOperator();
  const removeOperator = useRemoveRoomOperator();
  const operators = useRoomOperators();

  const [form, setForm] = useState<RoomDraft>(() => draftFrom(room));

  // THE BASELINE, and it is what `dirty` is measured against rather than the room prop. Measured
  // against the prop, a room step that succeeded would go on reading as unsaved until the list
  // refetched — and Save pressed in that window would patch a second time. Derived rather than
  // stored, the way the template editor's is: a successful save moves it, and nothing has to
  // remember that the save happened.
  const [baseline, setBaseline] = useState<RoomDraft>(() => draftFrom(room));

  const [pending, setPending] = useState<PendingOperators>(NOTHING_PENDING);
  const [applied, setApplied] = useState<SaveStep[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  // Same gate as the template dialog: a click can be muscle memory, typing the name cannot —
  // and this one destroys more than a template does.
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const set = <K extends keyof RoomDraft>(key: K, value: RoomDraft[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const creating = room === null;
  // NOT `creating || …`: registering a room is a fact about which step Save runs, not about
  // anything having been typed. Folded in, a Register dialog opened and closed straight away asked
  // about discarding work nobody had done.
  const roomDirty = !same(form, baseline);
  const unsaved = unsavedLabel({ roomDirty, pending });
  const dirty = unsaved !== null;

  const read = operatorReadOf(operators);
  const links = operators.data?.data ?? [];
  const portalUrl = roleGrantUrl(operators.data?.workforceTenantId);
  const listRoleNotice = roleNoticeText(operators.data?.roleNotice, 'operators');

  const deleteArmed = room !== null && deleteConfirmation.trim() === room.name;

  const plan = savePlan({ creating, roomDirty, pending });

  const stagedVerdict =
    pending.staged.length > 0 ? canLink(pending.staged) : { ok: true, reason: '' };
  const renameProblem = Object.values(pending.renames)
    .map((name) => nameProblem(name))
    .find((p) => p !== null);

  const fieldsValid = form.name.trim().length > 0 && form.logo.trim().length > 0;
  // A cleared time input reports '', which is not midnight — so it blocks rather than saves 00:00.
  const gamingDayMinutes = minutesOf(form.gamingDayStart);

  const blocker = !fieldsValid
    ? 'A room needs a name and a logo.'
    : gamingDayMinutes === null
      ? 'The gaming day needs a start time — 00:00 if it follows the calendar.'
    : !stagedVerdict.ok
      ? stagedVerdict.reason
      : (renameProblem ?? null);

  const error =
    failure ??
    describeApiError(createRoom.error) ??
    describeApiError(patchRoom.error) ??
    describeApiError(deleteRoom.error);

  /**
   * THE ONE EXIT THIS DIALOG CANNOT INTERCEPT ITSELF. admin-web has no central unsaved-work guard —
   * room-web's is a shell-level thing for a routed app with several exits, and this is one dialog —
   * so closing the tab is covered the way the points builder covers it.
   */
  useEffect(() => {
    if (!dirty) return undefined;

    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const attemptClose = () => {
    if (dirty) setConfirmDiscard(true);
    else onClose();
  };

  // Escape is a way out like any other, so it asks the same question — but it backs out of a
  // confirmation first, rather than answering one question with another. Bound on the window
  // rather than an element, because the focus may be anywhere inside the dialog, and rebound each
  // render so it closes over the current state rather than the state at mount.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;

      if (confirmDelete) setConfirmDelete(false);
      else if (confirmDiscard) setConfirmDiscard(false);
      else attemptClose();
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const roomBody = {
    ...form,
    name: form.name.trim(),
    location: form.location.trim(),
    timeZone: form.timeZone.trim(),
  };

  /**
   * Runs the plan in order, narrowing the draft as each step lands.
   *
   * <p>
   * <b>It stops at the first refusal, and everything before it stays applied.</b> That is not a
   * compromise — the room is a resource with its own lifecycle, and rolling a room's creation back
   * because a directory lookup refused one of twenty staged people would be the worse outcome. What
   * makes it recoverable is that the steps that succeeded are dropped, so Save again retries only
   * what is left.
   * </p>
   * <p>
   * <b>A failed ROOM step runs nothing else</b>, which falls out of the ordering rather than needing
   * a rule: it is the first step, and the loop breaks. On a create there is no room for the links to
   * point at; on a patch, applying operator changes to a room whose own save had just been refused
   * would be answering a question nobody asked.
   * </p>
   */
  const save = async () => {
    if (blocker !== null || saving || plan.length === 0) return;

    setFailure(null);
    setSaving(true);

    let roomId = room?.id ?? null;
    let left = pending;
    const landed: SaveStep[] = [];

    try {
      for (const step of plan) {
        switch (step.kind) {
          case 'create-room': {
            // status and defaultTags are only meaningful at creation, where the server's defaults
            // are the ones we want anyway. A blank time zone goes as null, so the server applies its
            // own default rather than being sent an empty id to refuse.
            const created = await createRoom.mutateAsync({
              ...requestOf(roomBody),
              status: 'active',
              defaultTags: [],
            });
            roomId = created.id;
            // Told at once rather than at the end, so that a refusal further down leaves the table
            // pointed at the room that now exists — and a retry patches rather than creating again.
            onCreated(created);
            break;
          }

          case 'patch-room':
            await patchRoom.mutateAsync({ id: room!.id, body: requestOf(roomBody) });
            break;

          case 'unlink':
            await removeOperator.mutateAsync(step.id);
            break;

          case 'rename':
            await renameOperator.mutateAsync({ id: step.id, name: step.name });
            break;

          case 'link':
            await linkOperators.mutateAsync({ roomId: roomId!, entries: step.entries });
            break;
        }

        landed.push(step);
        left = pendingAfter(left, step);
      }

      onClose();
    } catch (e) {
      setFailure(describeApiError(e) ?? 'Something went wrong. Nothing further was saved.');
    } finally {
      setPending(left);
      if (landed.some((s) => s.kind === 'create-room' || s.kind === 'patch-room')) {
        setBaseline(roomBody);
      }
      // Accumulated across attempts: a second Save that fails again does not make the first
      // attempt's writes untrue.
      setApplied((a) => [...a, ...landed]);
      setSaving(false);
    }
  };

  const landedNote = appliedNote(applied);
  const discarding = discardNote(pending);
  const linkNote = room ? operatorLinkNote(read, room.id) : 'no operator links';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/60 p-6"
      role="dialog"
      aria-modal="true"
      // The backdrop is a way out like Cancel and Escape, so it asks the same question. Guarded on
      // the target so a click that started inside the panel cannot close it on release.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) attemptClose();
      }}
    >
      <div className="max-h-full w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <h3 className="font-display text-lg font-semibold text-ink-950">
          {room ? 'Edit poker room' : 'Register new poker room'}
        </h3>

        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="mb-1 block text-xs text-ink-800/60">Room name</span>
            <input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="Room name"
              className={inputClass}
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-xs text-ink-800/60">Location</span>
            <input
              value={form.location}
              onChange={(e) => set('location', e.target.value)}
              placeholder="Location"
              className={inputClass}
            />
          </label>

          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs text-ink-800/60">Primary color</span>
              <input
                type="color"
                value={form.primaryColor}
                onChange={(e) => set('primaryColor', e.target.value)}
                className="h-10 w-full rounded-lg border border-ink-800/15"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-ink-800/60">Accent color</span>
              <input
                type="color"
                value={form.accentColor}
                onChange={(e) => set('accentColor', e.target.value)}
                className="h-10 w-full rounded-lg border border-ink-800/15"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-ink-800/60">Logo</span>
              <input
                value={form.logo}
                onChange={(e) => set('logo', e.target.value)}
                maxLength={2}
                aria-label="Room logo glyph"
                className={`${inputClass} h-10 text-center text-lg`}
              />
            </label>
          </div>

          <fieldset className="border-t border-ink-800/10 pt-3">
            <legend className="mb-2 text-xs text-ink-800/60">Enabled features</legend>
            <div className="space-y-2">
              <FeatureCheck icon={Award} label="Leaderboards" checked={form.canLeaderboard} onChange={(v) => set('canLeaderboard', v)} />
              <FeatureCheck icon={TrendingUp} label="Player ratings" checked={form.canRatings} onChange={(v) => set('canRatings', v)} />
              <FeatureCheck icon={Sparkles} label="Fantasy competitions" checked={form.canFantasy} onChange={(v) => set('canFantasy', v)} />
            </div>
            {room ? (
              <p className="mt-2 text-xs text-ink-800/50">
                Switching a feature off also hides the templates limited to it.
              </p>
            ) : null}
          </fieldset>

          <fieldset className="border-t border-ink-800/10 pt-3">
            <legend className="mb-2 text-xs text-ink-800/60">Time and reporting</legend>
            <div className="space-y-3">
              <label className="block">
                <span className="mb-1 block text-xs text-ink-800/60">Time zone</span>
                <input
                  value={form.timeZone}
                  onChange={(e) => set('timeZone', e.target.value)}
                  placeholder={room ? undefined : 'The platform default'}
                  list="room-time-zones"
                  spellCheck={false}
                  className={inputClass}
                />
                <datalist id="room-time-zones">
                  {knownZones.map((zone) => (
                    <option key={zone} value={zone} />
                  ))}
                </datalist>
                <span className="mt-1 block text-xs text-ink-800/50">
                  Where the room is, as a zone name such as America/New_York. Ratings recalculate on
                  this clock, and activity is counted by this room's days.
                </span>
              </label>

              <label className="block">
                <span className="mb-1 block text-xs text-ink-800/60">Gaming day starts at</span>
                <input
                  type="time"
                  value={form.gamingDayStart}
                  onChange={(e) => set('gamingDayStart', e.target.value)}
                  className="w-36 rounded-lg border border-ink-800/15 px-3 py-2 text-sm text-ink-950 outline-none focus:border-felt-500"
                />
                <span className="mt-1 block text-xs text-ink-800/50">
                  {gamingDayNote(gamingDayMinutes ?? 0)}
                </span>
              </label>

              {room ? (
                <p className="text-xs text-ink-800/50">
                  A change to the time zone or the gaming-day start applies from when it is saved.
                  Activity already recorded stays on the day it was counted under — it is never
                  re-sorted.
                </p>
              ) : null}

              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={form.isInternal}
                  onChange={(e) => set('isInternal', e.target.checked)}
                  className="mt-0.5"
                />
                <span className="text-sm text-ink-950">
                  Internal room
                  <span className="block text-xs text-ink-800/50">
                    For testing and demonstrations. Kept out of every trend and product metric from
                    the moment it is set; its audit trail and activity feed are unaffected. Testing
                    the player portal on staging and production happens in internal rooms.
                  </span>
                </span>
              </label>
            </div>
          </fieldset>

          <OperatorsSection
            roomId={room?.id ?? null}
            links={links}
            pending={pending}
            onChange={setPending}
            portalUrl={portalUrl}
            roleNotice={listRoleNotice}
            readOnly={saving}
          />

          <ErrorNotice message={error} />

          {/*
            AFTER A PARTIAL SAVE, CANCEL CANNOT DISCARD — the room exists, the removals happened. So
            the dialog says what landed, and the way out below is relabelled to stop promising
            something it cannot deliver.
          */}
          {landedNote ? (
            <p className="rounded-lg bg-felt-700/5 px-3 py-2 text-[11px] leading-relaxed text-felt-900">
              {landedNote}
            </p>
          ) : null}
        </div>

        {/* The confirmation takes the whole footer rather than sitting beside "Save changes":
            the two are opposite intentions, and a destructive one should not share a row with
            the button next to it. */}
        {room && confirmDelete ? (
          <div className="mt-6 space-y-3">
            <div>
              <p className="text-sm font-medium text-ink-950">Delete this room?</p>
              <p className="mt-1 text-xs text-ink-800/60">
                {/* Counts rather than "all data": an operator deciding this needs to know the
                    size of what goes, and the server already reports both figures. The operator
                    links are the third, and were the one part named without a number. */}
                This deletes {room.players.toLocaleString()}{' '}
                {room.players === 1 ? 'player' : 'players'} and {room.tournaments.toLocaleString()}{' '}
                {room.tournaments === 1 ? 'tournament' : 'tournaments'}, along with every result,
                leaderboard, rating, fantasy contest and template entry at this room, and{' '}
                {linkNote}. This cannot be undone. Type{' '}
                <span className="font-mono text-ink-950">{room.name}</span> to confirm.
              </p>
              {discarding ? (
                <p className="mt-1 text-xs text-chip-500">{discarding}</p>
              ) : null}
            </div>

            <input
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder={room.name}
              aria-label="Type the room name to confirm deletion"
              autoFocus
              className={`${inputClass} font-mono`}
            />

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setConfirmDelete(false);
                  setDeleteConfirmation('');
                }}
                className="flex-1 rounded-lg border border-ink-800/15 py-2 text-sm text-ink-800 hover:bg-ink-800/5"
              >
                Keep room
              </button>
              <button
                type="button"
                // Deleting never runs the plan: what is staged is discarded rather than applied,
                // which the sentence above says out loud.
                onClick={() => deleteRoom.mutate(room.id, { onSuccess: onClose })}
                disabled={!deleteArmed || deleteRoom.isPending}
                className="flex-1 rounded-lg border border-chip-500/40 py-2 text-sm font-medium text-chip-500 hover:bg-chip-500/5 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                {deleteRoom.isPending ? 'Deleting…' : 'Delete room'}
              </button>
            </div>
          </div>
        ) : confirmDiscard ? (
          <div className="mt-6 space-y-3">
            <div>
              <p className="text-sm font-medium text-ink-950">Discard unsaved changes?</p>
              <p className="mt-1 text-xs text-ink-800/60">
                {/* It names WHAT is unsaved, because "unsaved changes" over a dialog with a room
                    form and an operator list leaves somebody working out which half they lose. */}
                {unsaved} will be lost.
                {landedNote ? ' What has already been saved stays saved.' : ''}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setConfirmDiscard(false)}
                className="flex-1 rounded-lg border border-ink-800/15 py-2 text-sm text-ink-800 hover:bg-ink-800/5"
              >
                Keep editing
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-lg border border-chip-500/40 py-2 text-sm font-medium text-chip-500 hover:bg-chip-500/5"
              >
                Discard
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-2">
            <div className="flex gap-2">
              {room ? (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="rounded-lg border border-chip-500/40 px-4 py-2 text-sm text-chip-500 hover:bg-chip-500/5"
                >
                  Delete
                </button>
              ) : null}
              <button
                type="button"
                onClick={attemptClose}
                className="flex-1 rounded-lg border border-ink-800/15 py-2 text-sm text-ink-800 hover:bg-ink-800/5"
              >
                {/* Once anything has landed there is nothing left to cancel, and a button still
                    offering it would be promising a rollback this dialog cannot perform. */}
                {landedNote ? 'Close' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={() => void save()}
                disabled={blocker !== null || saving || plan.length === 0}
                className="flex-1 rounded-lg bg-felt-700 py-2 text-sm font-medium text-white hover:bg-felt-900 disabled:opacity-50"
              >
                {saving ? 'Saving…' : room ? 'Save changes' : 'Register'}
              </button>
            </div>
            {/* The reason, beside the disabled control — never a button that refuses silently. */}
            {blocker ? <p className="text-[11px] text-ink-800/60">{blocker}</p> : null}
            {!blocker && plan.length === 0 ? (
              <p className="text-[11px] text-ink-800/50">No changes to save.</p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function FeatureCheck({
  icon: Icon,
  label,
  checked,
  onChange,
}: {
  icon: LucideIcon;
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded"
      />
      <Icon className="size-4 text-ink-800/60" />
      <span className="text-sm text-ink-800">{label}</span>
    </label>
  );
}
